import {
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  UseFormReturn,
  useForm,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';
import {
  useNotify,
  usePrompt,
} from 'core/hooks';
import {
  PCCFeeScheduleLineDTO,
  PCCOrgActivationSettingsDTO,
  SetPCCOrgActivationSettingsRequestDTO,
  UserDTO,
} from 'dtos';
import {
  OrgPCCActivationStatus,
} from 'dtos/orgPCCActivationStatus';
import {
  isValidFeeAmount,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/FeeSchedule';
import {
  FeeScheduleLine,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/FeeSchedule/types';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import {
  useGetPCCActivationSettings,
  useSetPCCActivationSettings,
} from 'views/EMR/hooks/pcc';

export type PCCActivationForm = {
  tempoProvider: UserDTO | null;
  suicideAlertAssignee: UserDTO | null;
  tempoFeeSchedule: FeeScheduleLine[];
  ccmFeeSchedule: FeeScheduleLine[];
  rtmFeeSchedule: FeeScheduleLine[];
};

type UsePCCSettingsFormOptions = {
  getSuccessMessage?: (isActivated: boolean) => string | undefined;
  notifyOnSuccess?: boolean;
  onSuccess?: (response: PCCOrgActivationSettingsDTO) => void;
};

export type UsePCCSettingsFormResult = {
  formProps: UseFormReturn<PCCActivationForm>;
  control: UseFormReturn<PCCActivationForm>['control'];
  handleSubmit: UseFormReturn<PCCActivationForm>['handleSubmit'];
  isValid: boolean;
  isLoadingDefaults: boolean;
  isSaving: boolean;
  isFormDisabled: boolean;
  isActivated: boolean;
  showCcmFeeSchedule: boolean;
  isValidFeeSchedule: (feeSchedule: FeeScheduleLine[]) => boolean;
  onSubmit: (values: PCCActivationForm) => Promise<void>;
  resetSession: () => void;
};

const defaultPCCActivationForm: PCCActivationForm = {
  tempoProvider: null,
  suicideAlertAssignee: null,
  tempoFeeSchedule: [],
  ccmFeeSchedule: [],
  rtmFeeSchedule: [],
};

export function isValidFeeSchedule(feeSchedule: FeeScheduleLine[]) {
  return feeSchedule.every((feeLine) => (
    notEmptyString(feeLine.serviceCode)
    && isValidFeeAmount(feeLine.chargeAmount)
  ));
}

function normalizeFeeSchedule(
  feeSchedule: FeeScheduleLine[],
): PCCFeeScheduleLineDTO[] {
  return feeSchedule.map((feeLine) => ({
    serviceCode: feeLine.serviceCode,
    description: feeLine.description,
    chargeAmount: Number(feeLine.chargeAmount),
  }));
}

export function usePCCSettingsForm({
  getSuccessMessage,
  notifyOnSuccess = true,
  onSuccess,
}: UsePCCSettingsFormOptions = {}): UsePCCSettingsFormResult {
  const notify = useNotify();
  const {
    data: activationSettings,
    isLoading: isLoadingActivationSettings,
  } = useGetPCCActivationSettings();
  const {
    data: users = [],
    isLoading: isLoadingUsers,
  } = useGetUsers({ onlyDoctors: false });

  const providerByUserId = useMemo(() => keyBy(users, 'userId'), [users]);

  const {
    mutateAsync: setActivationSettings,
    isPending,
  } = useSetPCCActivationSettings();
  const formProps = useForm<PCCActivationForm>({
    defaultValues: defaultPCCActivationForm,
    mode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting, isDirty },
  } = formProps;

  usePrompt(isDirty);

  const isLoadingDefaults = isLoadingActivationSettings || isLoadingUsers;
  const isSaving = isPending || isSubmitting;
  const isFormDisabled = isLoadingDefaults || isSaving;
  const isActivated = activationSettings?.activationStatus
    === OrgPCCActivationStatus.Active;
  const showCcmFeeSchedule = activationSettings?.isCcmApplicable
    && (activationSettings?.ccmFeeSchedule ?? []).length > 0;

  useEffect(() => {
    if (isLoadingDefaults || isNil(activationSettings)) {
      return;
    }

    reset({
      tempoProvider: providerByUserId[activationSettings.tempo?.tempoProviderId ?? ''],
      suicideAlertAssignee: providerByUserId[activationSettings.tempo?.suicideAlertAssigneeId ?? ''],
      tempoFeeSchedule: activationSettings.tempo?.feeSchedule ?? [],
      ccmFeeSchedule: activationSettings.ccmFeeSchedule ?? [],
      rtmFeeSchedule: activationSettings.rtm?.feeSchedule ?? [],
      ...(showCcmFeeSchedule && {
        ccmFeeSchedule: activationSettings.ccmFeeSchedule ?? [],
      }),
    });
  }, [
    activationSettings,
    isLoadingDefaults,
    providerByUserId,
    reset,
    showCcmFeeSchedule,
  ]);

  const resetSession = useCallback(() => {
    if (isNil(activationSettings) || isLoadingDefaults) {
      reset(defaultPCCActivationForm);
      return;
    }

    reset({
      tempoProvider: providerByUserId[activationSettings.tempo?.tempoProviderId ?? ''],
      suicideAlertAssignee: providerByUserId[activationSettings.tempo?.suicideAlertAssigneeId ?? ''],
      tempoFeeSchedule: activationSettings.tempo?.feeSchedule ?? [],
      ccmFeeSchedule: activationSettings.ccmFeeSchedule ?? [],
      rtmFeeSchedule: activationSettings.rtm?.feeSchedule ?? [],
      ...(showCcmFeeSchedule && {
        ccmFeeSchedule: activationSettings.ccmFeeSchedule ?? [],
      }),
    });
  }, [
    activationSettings,
    isLoadingDefaults,
    providerByUserId,
    reset,
    showCcmFeeSchedule,
  ]);

  const onSubmit = useCallback(async (values: PCCActivationForm) => {
    const request: SetPCCOrgActivationSettingsRequestDTO = {
      tempo: {
        tempoProviderId: values.tempoProvider?.userId,
        suicideAlertAssigneeId: values.suicideAlertAssignee?.userId,
      },
      tempoFeeSchedule: normalizeFeeSchedule(values.tempoFeeSchedule),
      ...(showCcmFeeSchedule ? {
        ccmFeeSchedule: normalizeFeeSchedule(values.ccmFeeSchedule),
      } : { ccmFeeSchedule: [] }),
      rtmFeeSchedule: normalizeFeeSchedule(values.rtmFeeSchedule),
    };

    try {
      const response = await setActivationSettings(request);
      const successMessage = getSuccessMessage?.(isActivated);
      if (notifyOnSuccess && notEmptyString(successMessage)) {
        notify({
          variant: 'success',
          message: successMessage,
        });
      }
      onSuccess?.(response);
    } catch (error) {
      console.error(error);
    }
  }, [
    getSuccessMessage,
    isActivated,
    notify,
    notifyOnSuccess,
    onSuccess,
    setActivationSettings,
    showCcmFeeSchedule,
  ]);

  return {
    formProps,
    control,
    handleSubmit,
    isValid,
    isLoadingDefaults,
    isSaving,
    isFormDisabled,
    isActivated,
    showCcmFeeSchedule: Boolean(showCcmFeeSchedule),
    isValidFeeSchedule,
    onSubmit,
    resetSession,
  };
}
